import React, { useState } from 'react';
import { Card, Button, Input, Label, Select } from '../components/ui';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';

// Format date to DD/MM/YYYY
const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
};

export default function AccountOpeningForm() {
    const [values, setValues] = useState({
        bookNo: '',
        serialNo: '',
        solId: '38234501',
        depositScheme: '',
        postOfficeName: 'Krishnanagar',
        date: new Date().toISOString().split('T')[0],
        applicant1: {
            cifId: '',
            fullName: '',
            gender: 'Male',
            dob: '',
            panNumber: '',
            flatNo: '',
            street: '',
            postOffice: '',
            city: '',
            state: '',
            pinCode: '',
            mobileNo: '',
            photoIdType: 'Pan Card',
            photoIdNumber: '',
            addressProofType: 'Aadhaar Card',
            addressProofNumber: '',
        },
        applicant2: {
            cifId: '',
            fullName: '',
            gender: 'Male',
            dob: '',
            panNumber: '',
            flatNo: '',
            street: '',
            postOffice: '',
            city: '',
            state: '',
            pinCode: '',
            mobileNo: '',
            photoIdType: 'Pan Card',
            photoIdNumber: '',
            addressProofType: 'Aadhaar Card',
            addressProofNumber: '',
        },
        applicant3: {
            cifId: '',
            fullName: '',
            gender: 'Male',
            dob: '',
            panNumber: '',
            flatNo: '',
            street: '',
            postOffice: '',
            city: '',
            state: '',
            pinCode: '',
            mobileNo: '',
            photoIdType: 'Pan Card',
            photoIdNumber: '',
            addressProofType: 'Aadhaar Card',
            addressProofNumber: '',
        },
        guardianName: '',
        // operatingInstructions: {
        //     self: true,
        //     jointB: false,
        //     jointA: false,
        //     agent: true
        // },
        depositAmount: '',
        depositAmountWords: '',
        bankName: '',
        chequeNumber: '',
        chequeDate: '',
        nominee1: {
            name: '',
            dob: '',
            share: '',
            address: ''
        },
        nominee2: {
            name: '',
            dob: '',
            share: '',
            address: ''
        },
        accountNumber: '',
        agentName: ''
    });

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<{ url?: any; message?: string; } | null>(null);

    async function handleFill() {
        setLoading(true);
        setResult(null);
        try {
            // Format applicant DOBs
            const formattedApplicant2 = {
                ...values.applicant2,
                dob: values.applicant2.dob ? formatDate(values.applicant2.dob) : ''
            };
            const formattedApplicant3 = {
                ...values.applicant3,
                dob: values.applicant3.dob ? formatDate(values.applicant3.dob) : ''
            };

            const { applicant2, applicant3, ...restValues } = values;
            const finalValues = {
                ...restValues,
                date: restValues.date ? formatDate(restValues.date) : '',
                applicant1: {
                    ...restValues.applicant1,
                    dob: restValues.applicant1.dob ? formatDate(restValues.applicant1.dob) : ''
                },
                ...(formattedApplicant2.fullName.trim() && { applicant2: formattedApplicant2 }),
                ...(formattedApplicant3.fullName.trim() && { applicant3: formattedApplicant3 }),
            };

            const res = await fetch(`${API_BASE}/api/pdf/fill-account-opening-form`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...finalValues }),
            });
            const response = await res.json();
            if (res.ok) {
                setResult({
                    url: response.url || {},
                    message: response.message || 'Form filled successfully'
                });

                // Download the PDF file
                if (response?.url) {
                    const link = document.createElement('a');
                    const url = `${API_BASE}/${response.url}`;

                    // Fetch the PDF file as a blob
                    fetch(url)
                        .then(res => res.blob())
                        .then(blob => {
                            const blobUrl = window.URL.createObjectURL(blob);
                            link.href = blobUrl;
                            link.download = values.applicant1.fullName ?
                                `${values.applicant1.fullName.replace(/\s+/g, '_')}_form.pdf` :
                                'account_opening_form.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(blobUrl);
                        });
                }
            } else {
                alert(response.error || 'Failed to fill PDF');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <main className="container space-y-6">
            <h1 className="text-2xl font-semibold">Fill Account Opening Form</h1>
            <Card>
                <div className="space-y-6">
                    {/* Account Details Section */}
                    <div>
                        <h2 className="text-lg font-semibold">Account Details</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="bookNo">Book No</Label>
                                <Input
                                    id="bookNo"
                                    name="bookNo"
                                    value={values.bookNo}
                                    onChange={(e) => setValues({ ...values, bookNo: e.target.value })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="serialNo">Serial No</Label>
                                <Input
                                    id="serialNo"
                                    name="serialNo"
                                    value={values.serialNo}
                                    onChange={(e) => setValues({ ...values, serialNo: e.target.value })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="solId">SOL ID</Label>
                                <Input
                                    id="solId"
                                    name="solId"
                                    value={values.solId}
                                    onChange={(e) => setValues({ ...values, solId: e.target.value })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="depositScheme">Deposit Scheme</Label>
                                <Select
                                    id="depositScheme"
                                    name="depositScheme"
                                    value={values.depositScheme}
                                    onChange={(e) => setValues({ ...values, depositScheme: e.target.value })}
                                >
                                    <option value="">Select Deposit Scheme</option>
                                    <option value="1TD">1TD</option>
                                    <option value="2TD">2TD</option>
                                    <option value="NSC">NSC</option>
                                    <option value="RD">RD</option>
                                    <option value="MIS">MIS</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="postOfficeName">Post Office Name</Label>
                                <Input
                                    id="postOfficeName"
                                    name="postOfficeName"
                                    value={values.postOfficeName}
                                    onChange={(e) => setValues({ ...values, postOfficeName: e.target.value })}
                                    
                                />
                            </div>

                            <div>
                                <Label htmlFor="date">Date</Label>
                                <Input
                                    id="date"
                                    name="date"
                                    type="date"
                                    value={values.date}
                                    onChange={(e) => setValues({ ...values, date: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Applicant 1 Details */}
                    <div>
                        <h2 className="text-lg font-semibold">Applicant 1 Details</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="applicant1.cifId">CIF ID</Label>
                                <Input
                                    id="applicant1.cifId"
                                    name="applicant1.cifId"
                                    value={values.applicant1.cifId}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, cifId: e.target.value } })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="applicant1.fullName">Full Name</Label>
                                <Input
                                    id="applicant1.fullName"
                                    name="applicant1.fullName"
                                    value={values.applicant1.fullName}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, fullName: e.target.value.toUpperCase() } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.gender">Gender</Label>
                                <Select
                                    id="applicant1.gender"
                                    name="applicant1.gender"
                                    value={values.applicant1.gender}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, gender: e.target.value } })}
                                    required
                                >
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant1.dob">Date of Birth</Label>
                                <Input
                                    id="applicant1.dob"
                                    name="applicant1.dob"
                                    type="date"
                                    value={values.applicant1.dob}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, dob: e.target.value } })}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.panNumber">PAN Number</Label>
                                <Input
                                    id="applicant1.panNumber"
                                    name="applicant1.panNumber"
                                    value={values.applicant1.panNumber}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, panNumber: e.target.value, photoIdNumber: e.target.value } })}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.flatNo">Flat No</Label>
                                <Input
                                    id="applicant1.flatNo"
                                    name="applicant1.flatNo"
                                    value={values.applicant1.flatNo}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, flatNo: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.street">Street</Label>
                                <Input
                                    id="applicant1.street"
                                    name="applicant1.street"
                                    value={values.applicant1.street}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, street: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.postOffice">Post Office</Label>
                                <Input
                                    id="applicant1.postOffice"
                                    name="applicant1.postOffice"
                                    value={values.applicant1.postOffice}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, postOffice: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.city">City</Label>
                                <Input
                                    id="applicant1.city"
                                    name="applicant1.city"
                                    value={values.applicant1.city}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, city: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.state">State</Label>
                                <Input
                                    id="applicant1.state"
                                    name="applicant1.state"
                                    value={values.applicant1.state}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, state: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.pinCode">PIN Code</Label>
                                <Input
                                    id="applicant1.pinCode"
                                    name="applicant1.pinCode"
                                    value={values.applicant1.pinCode}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, pinCode: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.mobileNo">Mobile No</Label>
                                <Input
                                    id="applicant1.mobileNo"
                                    name="applicant1.mobileNo"
                                    value={values.applicant1.mobileNo}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, mobileNo: e.target.value } })}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.photoIdType">Photo ID Type</Label>
                                <Select
                                    id="applicant1.photoIdType"
                                    name="applicant1.photoIdType"
                                    value={values.applicant1.photoIdType}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, photoIdType: e.target.value } })}
                                    required
                                >
                                    <option value="Pan Card">Pan Card</option>
                                    <option value="Aadhaar Card">Aadhaar Card</option>
                                    <option value="Passport">Passport</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant1.photoIdNumber">Photo ID Number</Label>
                                <Input
                                    id="applicant1.photoIdNumber"
                                    name="applicant1.photoIdNumber"
                                    value={values.applicant1.photoIdNumber}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, photoIdNumber: e.target.value } })}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant1.addressProofType">Address Proof Type</Label>
                                <Select
                                    id="applicant1.addressProofType"
                                    name="applicant1.addressProofType"
                                    value={values.applicant1.addressProofType}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, addressProofType: e.target.value } })}
                                    required
                                >
                                    <option value="Aadhaar Card">Aadhaar Card</option>
                                    <option value="Voter ID">Voter ID</option>
                                    <option value="Utility Bill">Utility Bill</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant1.addressProofNumber">Address Proof Number</Label>
                                <Input
                                    id="applicant1.addressProofNumber"
                                    name="applicant1.addressProofNumber"
                                    value={values.applicant1.addressProofNumber}
                                    onChange={(e) => setValues({ ...values, applicant1: { ...values.applicant1, addressProofNumber: e.target.value } })}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    {/* Applicant 2 Details */}
                    <div>
                        <h2 className="text-lg font-semibold">Applicant 2 Details</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="applicant2.cifId">CIF ID</Label>
                                <Input
                                    id="applicant2.cifId"
                                    name="applicant2.cifId"
                                    value={values.applicant2.cifId}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, cifId: e.target.value } })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="applicant2.fullName">Full Name</Label>
                                <Input
                                    id="applicant2.fullName"
                                    name="applicant2.fullName"
                                    value={values.applicant2.fullName}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, fullName: e.target.value.toUpperCase() } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.gender">Gender</Label>
                                <Select
                                    id="applicant2.gender"
                                    name="applicant2.gender"
                                    value={values.applicant2.gender}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, gender: e.target.value } })}
                                >
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant2.dob">Date of Birth</Label>
                                <Input
                                    id="applicant2.dob"
                                    name="applicant2.dob"
                                    type="date"
                                    value={values.applicant2.dob}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, dob: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.panNumber">PAN Number</Label>
                                <Input
                                    id="applicant2.panNumber"
                                    name="applicant2.panNumber"
                                    value={values.applicant2.panNumber}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, panNumber: e.target.value, photoIdNumber: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.flatNo">Flat No</Label>
                                <Input
                                    id="applicant2.flatNo"
                                    name="applicant2.flatNo"
                                    value={values.applicant2.flatNo}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, flatNo: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.street">Street</Label>
                                <Input
                                    id="applicant2.street"
                                    name="applicant2.street"
                                    value={values.applicant2.street}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, street: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.postOffice">Post Office</Label>
                                <Input
                                    id="applicant2.postOffice"
                                    name="applicant2.postOffice"
                                    value={values.applicant2.postOffice}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, postOffice: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.city">City</Label>
                                <Input
                                    id="applicant2.city"
                                    name="applicant2.city"
                                    value={values.applicant2.city}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, city: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.state">State</Label>
                                <Input
                                    id="applicant2.state"
                                    name="applicant2.state"
                                    value={values.applicant2.state}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, state: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.pinCode">PIN Code</Label>
                                <Input
                                    id="applicant2.pinCode"
                                    name="applicant2.pinCode"
                                    value={values.applicant2.pinCode}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, pinCode: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.mobileNo">Mobile No</Label>
                                <Input
                                    id="applicant2.mobileNo"
                                    name="applicant2.mobileNo"
                                    value={values.applicant2.mobileNo}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, mobileNo: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.photoIdType">Photo ID Type</Label>
                                <Select
                                    id="applicant2.photoIdType"
                                    name="applicant2.photoIdType"
                                    value={values.applicant2.photoIdType}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, photoIdType: e.target.value } })}
                                >
                                    <option value="Pan Card">Pan Card</option>
                                    <option value="Aadhaar Card">Aadhaar Card</option>
                                    <option value="Passport">Passport</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant2.photoIdNumber">Photo ID Number</Label>
                                <Input
                                    id="applicant2.photoIdNumber"
                                    name="applicant2.photoIdNumber"
                                    value={values.applicant2.photoIdNumber}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, photoIdNumber: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant2.addressProofType">Address Proof Type</Label>
                                <Select
                                    id="applicant2.addressProofType"
                                    name="applicant2.addressProofType"
                                    value={values.applicant2.addressProofType}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, addressProofType: e.target.value } })}
                                >
                                    <option value="Aadhaar Card">Aadhaar Card</option>
                                    <option value="Voter ID">Voter ID</option>
                                    <option value="Utility Bill">Utility Bill</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant2.addressProofNumber">Address Proof Number</Label>
                                <Input
                                    id="applicant2.addressProofNumber"
                                    name="applicant2.addressProofNumber"
                                    value={values.applicant2.addressProofNumber}
                                    onChange={(e) => setValues({ ...values, applicant2: { ...values.applicant2, addressProofNumber: e.target.value } })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Applicant 3 Details */}
                    <div>
                        <h2 className="text-lg font-semibold">Applicant 3 Details</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="applicant3.cifId">CIF ID</Label>
                                <Input
                                    id="applicant3.cifId"
                                    name="applicant3.cifId"
                                    value={values.applicant3.cifId}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, cifId: e.target.value } })}
                                />
                            </div>
                            <div>
                                <Label htmlFor="applicant3.fullName">Full Name</Label>
                                <Input
                                    id="applicant3.fullName"
                                    name="applicant3.fullName"
                                    value={values.applicant3.fullName}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, fullName: e.target.value.toUpperCase() } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.gender">Gender</Label>
                                <Select
                                    id="applicant3.gender"
                                    name="applicant3.gender"
                                    value={values.applicant3.gender}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, gender: e.target.value } })}
                                >
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant3.dob">Date of Birth</Label>
                                <Input
                                    id="applicant3.dob"
                                    name="applicant3.dob"
                                    type="date"
                                    value={values.applicant3.dob}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, dob: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.panNumber">PAN Number</Label>
                                <Input
                                    id="applicant3.panNumber"
                                    name="applicant3.panNumber"
                                    value={values.applicant3.panNumber}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, panNumber: e.target.value, photoIdNumber: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.flatNo">Flat No</Label>
                                <Input
                                    id="applicant3.flatNo"
                                    name="applicant3.flatNo"
                                    value={values.applicant3.flatNo}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, flatNo: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.street">Street</Label>
                                <Input
                                    id="applicant3.street"
                                    name="applicant3.street"
                                    value={values.applicant3.street}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, street: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.postOffice">Post Office</Label>
                                <Input
                                    id="applicant3.postOffice"
                                    name="applicant3.postOffice"
                                    value={values.applicant3.postOffice}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, postOffice: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.city">City</Label>
                                <Input
                                    id="applicant3.city"
                                    name="applicant3.city"
                                    value={values.applicant3.city}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, city: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.state">State</Label>
                                <Input
                                    id="applicant3.state"
                                    name="applicant3.state"
                                    value={values.applicant3.state}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, state: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.pinCode">PIN Code</Label>
                                <Input
                                    id="applicant3.pinCode"
                                    name="applicant3.pinCode"
                                    value={values.applicant3.pinCode}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, pinCode: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.mobileNo">Mobile No</Label>
                                <Input
                                    id="applicant3.mobileNo"
                                    name="applicant3.mobileNo"
                                    value={values.applicant3.mobileNo}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, mobileNo: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.photoIdType">Photo ID Type</Label>
                                <Select
                                    id="applicant3.photoIdType"
                                    name="applicant3.photoIdType"
                                    value={values.applicant3.photoIdType}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, photoIdType: e.target.value } })}
                                >
                                    <option value="Pan Card">Pan Card</option>
                                    <option value="Aadhaar Card">Aadhaar Card</option>
                                    <option value="Passport">Passport</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant3.photoIdNumber">Photo ID Number</Label>
                                <Input
                                    id="applicant3.photoIdNumber"
                                    name="applicant3.photoIdNumber"
                                    value={values.applicant3.photoIdNumber}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, photoIdNumber: e.target.value } })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="applicant3.addressProofType">Address Proof Type</Label>
                                <Select
                                    id="applicant3.addressProofType"
                                    name="applicant3.addressProofType"
                                    value={values.applicant3.addressProofType}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, addressProofType: e.target.value } })}
                                >
                                    <option value="Aadhaar Card">Aadhaar Card</option>
                                    <option value="Voter ID">Voter ID</option>
                                    <option value="Utility Bill">Utility Bill</option>
                                </Select>
                            </div>

                            <div>
                                <Label htmlFor="applicant3.addressProofNumber">Address Proof Number</Label>
                                <Input
                                    id="applicant3.addressProofNumber"
                                    name="applicant3.addressProofNumber"
                                    value={values.applicant3.addressProofNumber}
                                    onChange={(e) => setValues({ ...values, applicant3: { ...values.applicant3, addressProofNumber: e.target.value } })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Guardian Details */}
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Guardian Details</h2>
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <Label htmlFor="guardianName">Guardian Name</Label>
                                <Input
                                    id="guardianName"
                                    name="guardianName"
                                    value={values.guardianName}
                                    onChange={(e) => setValues({ ...values, guardianName: e.target.value.toUpperCase() })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Operating Instructions */}
                    {/* <div>
                        <h2 className="text-lg font-semibold">Operating Instructions</h2>
                        <div className="flex gap-4">
                            <div className="flex items-center">
                                <Input
                                    id="self"
                                    name="self"
                                    type="checkbox"
                                    checked={values.operatingInstructions.self}
                                    onChange={(e) => setValues({ ...values, operatingInstructions: { ...values.operatingInstructions, self: e.target.checked } })}
                                />
                                <Label htmlFor="self" className="ml-2">Self</Label>
                            </div>

                            <div className="flex items-center">
                                <Input
                                    id="jointB"
                                    name="jointB"
                                    type="checkbox"
                                    checked={values.operatingInstructions.jointB}
                                    onChange={(e) => setValues({ ...values, operatingInstructions: { ...values.operatingInstructions, jointB: e.target.checked } })}
                                />
                                <Label htmlFor="jointB" className="ml-2">Joint B</Label>
                            </div>

                            <div className="flex items-center">
                                <Input
                                    id="jointA"
                                    name="jointA"
                                    type="checkbox"
                                    checked={values.operatingInstructions.jointA}
                                    onChange={(e) => setValues({ ...values, operatingInstructions: { ...values.operatingInstructions, jointA: e.target.checked } })}
                                />
                                <Label htmlFor="jointA" className="ml-2">Joint A</Label>
                            </div>

                            <div className="flex items-center">
                                <Input
                                    id="agent"
                                    name="agent"
                                    type="checkbox"
                                    checked={values.operatingInstructions.agent}
                                    onChange={(e) => setValues({ ...values, operatingInstructions: { ...values.operatingInstructions, agent: e.target.checked } })}
                                />
                                <Label htmlFor="agent" className="ml-2">Agent</Label>
                            </div>
                        </div>
                    </div> */}

                    {/* Deposit Details */}
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Deposit Details</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                            <div>
                                <Label htmlFor="depositAmount">Deposit Amount</Label>
                                <Input
                                    id="depositAmount"
                                    name="depositAmount"
                                    type="number"
                                    value={values.depositAmount}
                                    onChange={(e) => setValues({ ...values, depositAmount: e.target.value })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="depositAmountWords">Deposit Amount in Words</Label>
                                <Input
                                    id="depositAmountWords"
                                    name="depositAmountWords"
                                    value={values.depositAmountWords}
                                    onChange={(e) => setValues({ ...values, depositAmountWords: e.target.value.toUpperCase() })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bank Details */}
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Bank Details</h2>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                            <div>
                                <Label htmlFor="bankName">Bank Name</Label>
                                <Input
                                    id="bankName"
                                    name="bankName"
                                    value={values.bankName}
                                    onChange={(e) => setValues({ ...values, bankName: e.target.value })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="chequeNumber">Cheque Number</Label>
                                <Input
                                    id="chequeNumber"
                                    name="chequeNumber"
                                    value={values.chequeNumber}
                                    onChange={(e) => setValues({ ...values, chequeNumber: e.target.value })}
                                />
                            </div>

                            <div>
                                <Label htmlFor="chequeDate">Cheque Date</Label>
                                <Input
                                    id="chequeDate"
                                    name="chequeDate"
                                    type="date"
                                    value={values.chequeDate}
                                    onChange={(e) => setValues({ ...values, chequeDate: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Nominee Details */}
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Nominee Details</h2>
                        <div className="space-y-8">
                            {/* Nominee 1 */}
                            <div>
                                <h3 className="text-md font-medium mb-4">Nominee 1</h3>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="nominee1.name">Full Name</Label>
                                        <Input
                                            id="nominee1.name"
                                            name="nominee1.name"
                                            value={values.nominee1.name}
                                            onChange={(e) => setValues({ ...values, nominee1: { ...values.nominee1, name: e.target.value.toUpperCase() } })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="nominee1.dob">Date of Birth</Label>
                                        <Input
                                            id="nominee1.dob"
                                            name="nominee1.dob"
                                            type="date"
                                            value={values.nominee1.dob}
                                            onChange={(e) => setValues({ ...values, nominee1: { ...values.nominee1, dob: e.target.value } })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="nominee1.share">Share (%)</Label>
                                        <Input
                                            id="nominee1.share"
                                            name="nominee1.share"
                                            type="text"
                                            value={values.nominee1.share}
                                            onChange={(e) => setValues({ ...values, nominee1: { ...values.nominee1, share: e.target.value } })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="nominee1.address">Address</Label>
                                        <Input
                                            id="nominee1.address"
                                            name="nominee1.address"
                                            value={values.nominee1.address}
                                            onChange={(e) => setValues({ ...values, nominee1: { ...values.nominee1, address: e.target.value} })}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Nominee 2 */}
                            <div>
                                <h3 className="text-md font-medium mb-4">Nominee 2</h3>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="nominee2.name">Full Name</Label>
                                        <Input
                                            id="nominee2.name"
                                            name="nominee2.name"
                                            value={values.nominee2.name}
                                            onChange={(e) => setValues({ ...values, nominee2: { ...values.nominee2, name: e.target.value.toUpperCase() } })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="nominee2.dob">Date of Birth</Label>
                                        <Input
                                            id="nominee2.dob"
                                            name="nominee2.dob"
                                            type="date"
                                            value={values.nominee2.dob}
                                            onChange={(e) => setValues({ ...values, nominee2: { ...values.nominee2, dob: e.target.value } })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="nominee2.share">Share (%)</Label>
                                        <Input
                                            id="nominee2.share"
                                            name="nominee2.share"
                                            type="text"
                                            value={values.nominee2.share}
                                            onChange={(e) => setValues({ ...values, nominee2: { ...values.nominee2, share: e.target.value } })}
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="nominee2.address">Address</Label>
                                        <Input
                                            id="nominee2.address"
                                            name="nominee2.address"
                                            value={values.nominee2.address}
                                            onChange={(e) => setValues({ ...values, nominee2: { ...values.nominee2, address: e.target.value } })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Account Number Section */}
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Account Details</h2>
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <Label htmlFor="accountNumber">Account Number</Label>
                                <Input
                                    id="accountNumber"
                                    name="accountNumber"
                                    value={values.accountNumber}
                                    onChange={(e) => setValues({ ...values, accountNumber: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Agent Details */}
                    <div>
                        <h2 className="text-lg font-semibold mb-4">Agent Details</h2>
                        <div className="grid grid-cols-1 gap-6">
                            <div>
                                <Label htmlFor="agentName">Agent Name</Label>
                                <Input
                                    id="agentName"
                                    name="agentName"
                                    value={values.agentName}
                                    onChange={(e) => setValues({ ...values, agentName: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center mt-8">
                        <Button 
                            type="submit" 
                            onClick={handleFill} 
                            disabled={loading}
                            className="w-full sm:w-auto px-8 py-3 text-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md"
                        >
                            {loading ? 'Processing...' : 'Generate & Download PDF'}
                        </Button>
                    </div>

                    {result?.message && (
                        <div className="mt-4 text-center">
                            <p className="text-green-600 font-medium">{result.message}</p>
                        </div>
                    )}
                </div>
            </Card>
        </main>
    );
}